<template>
  <div class="request" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark no-padding">
      <div class="local-hero-graphic">
        <div class="local-hero-header-wrapper">
          <h1 class="local-hero-header" v-translate>Make a grant request</h1>
        </div>
        <div class="local-hero-graphic-wrapper hidden-mobile">
          <div class="local-hero-graphic-holder">
            <PorchSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>

    <!-- Request Section -->
    <div class="cta-section-wrapper">
      <div
        class="cta-section"
        :class="{ 'cta-section-ineligible': !isEligible }"
        v-if="organizationDetails.isDefaultRequestOrganization"
      >
        <h1 v-translate>Request hardship assistance</h1>
        <h4 v-if="isEligible">
          <translate
            >If an unexpected emergency has made it hard for you financially, request the money you
            need to get back on track.</translate
          >
        </h4>
        <h4
          v-else
          v-translate="{
            orgName: organizationDetails.name,
            periodLength: organizationDetails.eligibilityPeriodNumDays,
            daysLeft: user.daysUntilEligible,
            infoEmail: organizationDetails.supportEmail,
          }"
        >
          %{orgName} requires that you remain with the company for %{periodLength} days before you
          are eligible to request a grant. You have %{daysLeft} remaining until you become eligible.
          Reach out to %{infoEmail} with any questions, issues, or feedback.
        </h4>
        <button
          @click="startRequest"
          class="form-button form-button-160"
          :disabled="!isEligible"
          :class="{ 'form-button-disabled': !isEligible }"
        >
          <span v-translate>Start</span>
        </button>
      </div>

      <!-- Repro rights Section -->
      <div v-if="organizationDetails.isReproductiveRightsOrganization" class="cta-section">
        <h1 v-translate>Request inclusive care assistance</h1>
        <h4 v-translate>
          If you need financial support to travel for reproductive healthcare of gender-affirming
          care, please request the money you need to move forward.
        </h4>
        <button @click="startReproRightsRequest" class="form-button form-button-160">
          <span v-translate>Start</span>
        </button>
      </div>
    </div>

    <!-- Modals -->
    <!-- Grant limit modal -->
    <GrantLimitModal :show="grantLimitModal" />

    <!-- Eligibility message modal -->
    <Modal
      v-if="!isEligible && eligibilityModal"
      dimmed
      :width="95"
      @close-modal="
        eligibilityModal = false;
        $router.push({ name: 'Home' })
      "
    >
      <div class="form-container card-shape modal-window">
        <h2 class="form-field-header" v-translate>
          You are not yet eligible to make a grant request.
        </h2>
        <div class="form-field-container">
          <p
            v-translate="{
              orgName: organizationDetails.name,
              periodLength: organizationDetails.eligibilityPeriodNumDays,
              daysLeft: user.daysUntilEligible,
              infoEmail: organizationDetails.supportEmail,
            }"
          >
            %{orgName} requires that you remain with the company for %{periodLength} days before you
            are eligible to request a grant. You have %{daysLeft} remaining until you become
            eligible. Reach out to %{infoEmail} with any questions, issues, or feedback.
          </p>
        </div>
        <div class="button-container">
          <button class="form-button" @click="$router.push({ name: 'Home' })">
            <span v-translate>I Understand</span>
          </button>
        </div>
      </div>
    </Modal>

    <!-- Required post-registration onboarding surveys -->
    <RequiredOnboardingSurveysModal :show="requiredOnboardingSurveysModal && !eligibilityModal && !grantLimitModal" />
  </div>
</template>

<script>
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import { mapState } from 'vuex'
import LoadSpinner from '@/components/LoadSpinner'
import { analyticsMixin } from '@/services/googleAnalytics'
import Action from '@/services/actions'
import CollectionManager from '@/services/collectionManager'
import ActivityCard from '@/components/myActivity/ActivityCard'
import Modal from '@/components/Modal'
import { customVariablesFilter } from '@/services/organizationCustomVariables'
import { translateToCurrentLang, formatText } from '@/services/utils'
import UserEvent from '@/services/userEvents/models'
import SurveyResponse from '@/services/surveyresponses'
import PorchSvg from '@/views/svg/PorchSvg'
import SubmitButton from '@/components/requests/SubmitButton'
import GrantLimitModal from '@/views/common/GrantLimitModal'
import RequiredOnboardingSurveysModal from '@/views/common/RequiredOnboardingSurveysModal'

const REQUEST_STARTED = 'REQUEST_STARTED'
const STARTED = 'STARTED'

export default {
  name: 'Request',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    ActivityCard,
    Modal,
    PorchSvg,
    SubmitButton,
    GrantLimitModal,
    RequiredOnboardingSurveysModal,
  },
  data() {
    return {
      today: new Date(),
      loading: false,
      startedRequests: CollectionManager.create({
        ModelClass: Action,
        filters: {
          ordering: '-datetime_created',
          actionType: REQUEST_STARTED,
          requestStatus: STARTED,
        },
      }),
      showResumeRequestModal: false,
      grantLimitModal: false,
      eligibilityModal: false,
      reproductiveRightsRequest: false,
    }
  },
  async beforeCreate() {
    if (Object.keys(this.$route.params).includes('reproductive_rights')) {
      this.reproductiveRightsRequest = true
    }
  },
  async created() {
    if (!this.maxedGrantLimit && this.user.isEligible) {
      this.startedRequests.refresh().then(() => {
        if (this.startedRequests.list.length) {
          this.trackCustomPageview('ResumeStartedRequest', '/resume-started-request')

          this.showResumeRequestModal = true
        }
      })
    } else if (await this.blockRequest()) {
    }
  },
  methods: {
    closeModal() {
      this.analyticsEventsTracker(
        'clicked-cta',
        'start a new request',
        'ResumeStartedRequest',
        '/resume-started-request',
      )
      this.showResumeRequestModal = false
    },
    async blockRequest() {
      if (this.maxedGrantLimit) {
        this.grantLimitModal = true
        await UserEvent.api.requestStartBlocked()
        this.trackCustomPageview('RequestStartBlockedMaxedGrantLimit', '/request-start-blocked')
        return true
      } else if (!this.user.isEligible) {
        this.eligibilityModal = true
        return true
      }
      return false
    },
    onClickRequestGrantMoney() {
      if (this.userIsLoggedIn) {
        this.$router.push({ name: 'StartRequest' })
      } else {
        this.$router.push({ name: 'Qualification' })
      }
    },
    async startReproRightsRequest() {
      if (await this.blockRequest()) return

      this.trackClickedCTA()

      const newRequest = await Request.api
        .create({})
        // Update user's current request in state
        .then((request) => {
          this.$store.dispatch('updateCurrentRequest', request)
          return request
        })

      this.$router.push({ name: 'ReceiveMoney', params: { reproductive_rights: true } })
    },
    async startRequest() {
      if (await this.blockRequest()) return

      this.loading = true

      this.trackClickedCTA()

      await Request.api
        .create({})
        // Update user's current request in state
        .then((request) => {
          this.$store.dispatch('updateCurrentRequest', request)
        })

      this.$router.push({ name: 'NewRequest' })
    },
    async onClickRequestReproRightsMoney() {
      if (this.userIsLoggedIn) {
        const newRequest = await Request.api
          .create({})
          // Update user's current request in state
          .then((request) => {
            this.$store.dispatch('updateCurrentRequest', request)
            return request
          })

        const eventCategoryList = await EventCategory.api.list({
          filters: { event_category_organization: this.user.organization },
        })

        const reproductiveRightsCategory = eventCategoryList.results.find((eventCategory) => {
          return eventCategory.name === 'Reproductive Rights'
        })

        if (!reproductiveRightsCategory) {
          throw 'Cant find repro rights event category'
        }

        const request = Request.create({
          ...newRequest,
          eventCategories: [reproductiveRightsCategory.id],
        })
        const updateFields = ['eventCategories']
        const response = await Request.api.update(request, updateFields)
        this.$store.dispatch('updateCurrentRequest', response)

        this.$router.push({ name: 'ReceiveMoney', params: { reproductive_rights: true } })
      } else {
        this.$router.push({ name: 'Qualification' })
      }
    },
    getScopedOrgProperty(propName) {
      // Empty strings, zero etc. are considered null/undefined/not-set
      const orgValue = this.organizationDetails && this.organizationDetails[propName]
      const subOrgValue = this.user.subOrganizationRef && this.user.subOrganizationRef[propName]

      return subOrgValue || orgValue
    },
  },
  computed: {
    ...mapState(['user', 'currentRequest', 'organizationDetails', 'defaultRequestFlow']),
    isEligible() {
      if (this.user) {
        return this.user.isEligible
      }
      return true
    },
    disabled() {
      if (this.loading) {
        return true
      }
      return false
    },
    hardshipDefinition() {
      return this.organizationDetails.hardshipDefinitionCopy
    },
    maxedGrantLimit() {
      const amountLimit = this.getScopedOrgProperty('grantLimit')
      const countLimit = this.getScopedOrgProperty('grantCountLimit')

      const hasLimits = amountLimit || countLimit

      if (!hasLimits) { return false }

      // Lifetime grant limit
      if (this.maxedLifetimeGrantLimit) {
        return true 
      }

      const currentAmount = this.user.totalPayoutsSinceLastGrantLimitReset
      const currentCount = this.user.totalNumberPayoutsSinceLastGrantLimitReset

      const overAmount = amountLimit ? currentAmount >= amountLimit : false
      const overCount = countLimit ? currentCount >= countLimit : false

      return overAmount || overCount
    },
    maxedLifetimeGrantLimit() {
      const amountLimitLifetime = this.getScopedOrgProperty('grantLimitLifetime')
      const currentAmountLifetime = this.user.totalPayouts
      if(amountLimitLifetime) {
        return currentAmountLifetime >= amountLimitLifetime
      }

      return false
    },
    requiredOnboardingSurveysModal() {
      if(!this.user)
        return false

      return !this.user.hasCompletedRequiredOnboardingSurveys
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/flex-box';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/grant-circles';

.maxedModalSection {
  margin: 1rem 0 0 0;
  justify-content: start;
  align-items: center;
}

.request {
  display: flex;
  flex-direction: column;

  .request-form {
    max-width: 704px;
    align-self: center;

    @include desktop-screen {
      max-width: 1076px;
      align-self: center;
    }

    text-align: left;

    .eligibility-list {
      ::v-deep p:first-of-type {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 2rem;
      }

      ::v-deep ul {
        display: flex;
        flex-direction: column;
        list-style: none;
        padding-left: 0.5rem;
      }

      ::v-deep li {
        display: flex;
        flex-direction: row;
        font-size: 1rem;
        line-height: 1.5rem;
        margin-bottom: 1rem;
        margin-left: 0;

        &::before {
          background: $black;
          border-radius: 50%;
          content: '';
          position: relative;
          top: 6px;
          height: 12px;
          width: 12px;
          min-width: 12px;
          max-width: 12px;
          margin-right: 1rem;
        }
      }
    }

    .your-commitment {
      @extend .card-shape;

      @include tablet-and-up-screen {
        padding: 2.5rem;
      }

      .your-commitment-heading {
        font-size: 1.5rem;
        line-height: 2rem;
        font-weight: bold;
      }

      .checkbox span.form-checkbox + span {
        font-family: 'Helvetica Neue';
        font-size: 18px;
        line-height: 28px;
      }
    }

    .button-container {
      margin-bottom: 0;
    }
  }
}

.activity-container {
  width: 90%;
  border-top: 1px solid #dfdfe0;
  border-bottom: 1px solid #dfdfe0;
}

.cta-section-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin: 0rem 1rem;

  @include tablet-screen {
    flex-direction: row;
    margin: 2.5rem 2rem;

    .cta-section {
      width: 50%;
      align-self: stretch;
      margin-bottom: 2.5rem;

      &:nth-of-type(1) {
        margin-right: 1rem;
      }

      &:nth-of-type(2) {
        margin-left: 1rem;
      }
    }
  }

  @include desktop-screen {
    flex-direction: row;
    padding: 4rem 0;

    .cta-section {
      max-width: 420px;
      margin-top: 0;
      padding: 0;
      align-self: stretch;

      &.cta-section-ineligible {
        max-width: 600px;
      }

      h1 {
        font-size: 2.25rem;
      }
    }
  }
}

.cta-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 2.5rem;
  align-self: center;
  max-width: 90%;
  justify-content: space-between;

  h1 {
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 2rem;
  }

  h4 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 2rem;
  }

  h5 {
    margin-bottom: 0;

    span {
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }

  .cta-section-ineligible {
  }
}
</style>
